import { useState, useRef, useEffect } from "react";
import { Send, Bot, User, Clock, Shield, CheckCircle, Gavel, FileX, Ban, Network, DollarSign, Scale, EyeOff, AlertCircle, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { llmChatService, ChatResponse } from "../src/services/llmChatService";

interface CaseData {
  id: string;
  title: string;
  type: string;
  urls: string[];
  createdAt: Date;
  status: "active" | "completed" | "pending";
  client_name?: string;
  case_name?: string;
}

interface Message {
  id: string;
  type: "user" | "assistant" | "system" | "error";
  content: string;
  timestamp: Date;
  metadata?: {
    confidence?: number;
    jurisdiction?: string;
    urgency?: "low" | "medium" | "high";
    actionItems?: string[];
    urlAnalysis?: {
      hostname: string;
      hosting: string;
      monetization: string[];
      jurisdiction: string;
    };
  };
  // LLM API Response data
  llmResponse?: {
    context_loaded: boolean;
    sources_used: Array<{
      filename: string;
      content_type: string;
      relevance_score: number;
    }>;
    package_info?: {
      client_name: string;
      case_name: string;
      domains_analyzed: string[];
      files_available: string[] | number;
    };
  };
  error?: string;
}

interface ChatInterfaceProps {
  activeCase: CaseData;
  messages: Message[];
  onUpdateMessages: (messages: Message[]) => void;
}

export function ChatInterface({ activeCase, messages, onUpdateMessages }: ChatInterfaceProps) {
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Mock/fallback AI response generator - useful for testing or fallback scenarios
  const generateAIResponse = (message: string, caseType: string): string => {
    const responses = {
      csam: [
        "I've analyzed the content and identified several concerning elements. Based on the case type, I recommend immediate evidence preservation and coordination with law enforcement.",
        "The hosting infrastructure appears to be using privacy-focused services. I can help you draft the appropriate legal requests for user data and logs.",
        "I've identified potential payment processors and advertising networks. This information could be valuable for financial investigations."
      ],
      fraud: [
        "I've detected several red flags in the financial flow analysis. The payment methods and hosting patterns suggest organized fraud operations.",
        "The domain registration and hosting history shows suspicious patterns typical of fraudulent operations.",
        "I recommend focusing on the financial forensics and payment processor subpoenas for this type of case."
      ],
      piracy: [
        "The content distribution network analysis reveals the scope of the piracy operation. I can help you identify all related infrastructure.",
        "I've mapped the monetization methods including advertising networks and subscription services.",
        "The hosting and CDN analysis shows this is a sophisticated operation with multiple revenue streams."
      ],
      cybercrime: [
        "I've analyzed the technical infrastructure and identified several security vulnerabilities and attack vectors.",
        "The network analysis reveals connections to known cybercrime infrastructure and command-and-control servers.",
        "I can help you trace the digital footprint and identify related criminal activities."
      ],
      other: [
        "I've completed the initial analysis of the provided URLs and identified several areas for further investigation.",
        "Based on the case parameters, I recommend focusing on the technical infrastructure and user data preservation.",
        "I can help you develop an investigation strategy tailored to your specific case requirements."
      ]
    };

    const typeResponses = responses[caseType as keyof typeof responses] || responses.other;
    const randomResponse = typeResponses[Math.floor(Math.random() * typeResponses.length)];
    
    // Add context from the user's message to make response more relevant
    const contextualResponse = message.toLowerCase().includes("help") 
      ? `I understand you need assistance. ${randomResponse}`
      : randomResponse;
    
    return contextualResponse;
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isLoading) return;

    // Store the message content before clearing it
    const messageContent = newMessage.trim();
    console.log('🚀 Starting handleSendMessage with:', { messageContent, activeCase: activeCase?.id });

    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      type: "user",
      content: messageContent,
      timestamp: new Date()
    };

    const updatedMessages = [...messages, userMessage];
    console.log('💬 Adding user message, total messages:', updatedMessages.length);
    
    try {
      onUpdateMessages(updatedMessages);
      setNewMessage("");
      setIsLoading(true);
    } catch (error) {
      console.error('❌ Error in initial message setup:', error);
      throw error; // Re-throw to trigger error boundary
    }

    try {
      // Use the case title/id as the case name for the API call
      const caseName = activeCase?.id || 'unknown-case';
      console.log('🔧 Preparing LLM API call for case:', caseName);

      // Convert message history to API format
      const conversationHistory = llmChatService.convertToApiHistory(messages || []);
      console.log('📜 Conversation history length:', conversationHistory.length);

      // Call the LLM API
      console.log('🌐 Calling LLM API...');
      const response: ChatResponse = await llmChatService.sendChatMessage({
        case_name: caseName,
        user_message: messageContent, // Use stored message content
        conversation_history: conversationHistory
      });
      console.log('✅ LLM API response received:', { hasResponse: !!response, responseLength: response?.response?.length });

      // Validate response before creating message
      if (!response || typeof response.response !== 'string') {
        throw new Error('Invalid response format from LLM API');
      }

      // Create assistant message with LLM response
      console.log('🤖 Creating assistant message...');
      const assistantMessage: Message = {
        id: `msg-${Date.now() + 1}`,
        type: "assistant",
        content: response.response || 'No response received',
        timestamp: new Date(),
        llmResponse: {
          context_loaded: response.context_loaded ?? false,
          sources_used: response.sources_used || [],
          package_info: response.package_info
        }
      };

      console.log('💾 Updating messages with assistant response...');
      onUpdateMessages([...updatedMessages, assistantMessage]);
      console.log('✅ Message update completed successfully');

    } catch (error) {
      console.error('❌ Error in LLM message handling:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        activeCase: activeCase?.id,
        messageContent: messageContent?.substring(0, 50) + '...'
      });
      
      try {
        // Fallback to mock response for development/testing
        console.log('🔄 Attempting fallback response...');
        const fallbackResponse = generateAIResponse(messageContent, activeCase.type);
        
        const fallbackMessage: Message = {
          id: `msg-${Date.now() + 1}`,
          type: "assistant",
          content: `[Fallback Response] ${fallbackResponse}`,
          timestamp: new Date(),
          metadata: {
            confidence: 60,
            urgency: "medium"
          }
        };

        console.log('💾 Adding fallback message...');
        onUpdateMessages([...updatedMessages, fallbackMessage]);
        console.log('✅ Fallback message added successfully');
      } catch (fallbackError) {
        console.error('❌ Error in fallback handling:', fallbackError);
        // Re-throw the original error to trigger error boundary
        throw error;
      }
    } finally {
      console.log('🏁 Setting loading to false');
      setIsLoading(false);
    }
  };





  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessageContent = (content: string) => {
    // Compact, polished formatting for chat messages
    return content
      // First, clean up any trailing backticks or unmatched markdown
      .replace(/`+$/g, '') // Remove trailing backticks
      .replace(/```+$/gm, '') // Remove trailing code block markers
      .replace(/\*+$/g, '') // Remove trailing asterisks
      // Handle code blocks first (before inline code)
      .replace(/```([^`]+)```/g, '<pre class="bg-muted p-2 rounded text-xs font-mono overflow-x-auto my-2"><code>$1</code></pre>')
      // Handle inline code (single backticks)
      .replace(/`([^`]+)`/g, '<code class="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">$1</code>')
      // Clean up any remaining unmatched backticks
      .replace(/`/g, '')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/^## (.*$)/gm, '<h3 class="text-base font-semibold mt-3 mb-2 text-foreground">$1</h3>')
      .replace(/^### (.*$)/gm, '<h4 class="text-sm font-semibold mt-2 mb-1.5 text-foreground">$1</h4>')
      .replace(/^• (.*$)/gm, '<div class="flex items-start gap-1.5 my-0 text-sm"><span class="text-primary mt-0.5 text-xs leading-none">•</span><span class="leading-snug">$1</span></div>')
      .replace(/^(\d+)\. (.*$)/gm, '<div class="flex items-start gap-1.5 my-0 text-sm"><span class="text-primary font-medium mt-0 text-xs leading-none">$1.</span><span class="leading-snug">$2</span></div>')
      .replace(/\n\n/g, '<div class="h-2"></div>')
      .replace(/\n/g, '<br/>')
      // Final cleanup: remove any remaining markdown artifacts
      .trim();
  };

  const getUrgencyColor = (urgency?: string) => {
    switch (urgency) {
      case "high": return "text-red-400";
      case "medium": return "text-yellow-400";
      case "low": return "text-green-400";
      default: return "text-muted-foreground";
    }
  };

  const quickActions = [
    { icon: Gavel, label: "Legal Actions", description: "Warrants, subpoenas, legal process" },
    { icon: Network, label: "Infrastructure", description: "Hosting, domains, technical analysis" },
    { icon: Shield, label: "Evidence Collection", description: "Preservation, collection, chain of custody" },
    { icon: FileX, label: "Case Report", description: "Generate summary, brief, or formal report" },
    { icon: Ban, label: "Take-down Actions", description: "Hosting notices, domain seizure" },
    { icon: DollarSign, label: "Financial Analysis", description: "Payment processors, cryptocurrency" }
  ];

  // Specific action buttons as requested
  const actionButtons = [
    { 
      icon: Scale, 
      label: "Litigation Hold", 
      prompt: "Help me prepare a litigation hold request for this case",
      color: "bg-blue-500/10 text-blue-400 border-blue-500/20 hover:bg-blue-500/20"
    },
    { 
      icon: Gavel, 
      label: "Subpoena", 
      prompt: "Draft a subpoena request for hosting provider data and user information",
      color: "bg-purple-500/10 text-purple-400 border-purple-500/20 hover:bg-purple-500/20"
    },
    { 
      icon: Ban, 
      label: "Takedown", 
      prompt: "Coordinate takedown actions for the target URLs in this case",
      color: "bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20"
    },
    { 
      icon: Shield, 
      label: "Evidence Preservation", 
      prompt: "Issue evidence preservation notices to prevent data destruction",
      color: "bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20"
    },
    { 
      icon: Network, 
      label: "Explore Infrastructure", 
      prompt: "Analyze the technical infrastructure and hosting patterns for this case",
      color: "bg-orange-500/10 text-orange-400 border-orange-500/20 hover:bg-orange-500/20"
    },
    { 
      icon: DollarSign, 
      label: "Demonetize", 
      prompt: "Target payment processors and advertising networks to disrupt monetization",
      color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20 hover:bg-yellow-500/20"
    },
    { 
      icon: EyeOff, 
      label: "Deindex", 
      prompt: "Request search engine deindexing and content removal from major platforms",
      color: "bg-gray-500/10 text-gray-400 border-gray-500/20 hover:bg-gray-500/20"
    }
  ];

  const handleActionClick = (prompt: string) => {
    setNewMessage(prompt);
    textareaRef.current?.focus();
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-6 max-w-2xl">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Bot className="w-8 h-8 text-primary" />
              </div>
              <div className="space-y-2">
                <h3>Ready to Assist with Your Investigation</h3>
                <p className="text-muted-foreground">
                  I'm here to help with legal analysis, technical investigation, evidence collection, and case reporting for <strong>{activeCase.title}</strong>.
                </p>
              </div>
              
              {/* Quick Action Buttons */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className="h-auto min-h-[120px] p-3 flex flex-col items-center gap-2 hover:bg-accent/50 transition-all duration-200 hover:scale-[1.02] text-wrap"
                    onClick={() => setNewMessage(`Help me with ${action.label.toLowerCase()} for this case`)}
                  >
                    <action.icon className="w-5 h-5 text-primary flex-shrink-0" />
                    <div className="text-center space-y-1 flex-1 flex flex-col justify-center w-full">
                      <div className="font-medium text-sm leading-tight break-words overflow-wrap-anywhere">{action.label}</div>
                      <div className="text-xs text-muted-foreground leading-tight break-words overflow-wrap-anywhere whitespace-normal">{action.description}</div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-3xl ${message.type === "user" ? "mr-4" : "mr-12"}`}>
                  <div className={`flex items-start gap-3 ${message.type === "user" ? "flex-row-reverse" : ""}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.type === "user" 
                        ? "bg-primary text-primary-foreground" 
                        : message.type === "system"
                        ? "bg-muted text-muted-foreground"
                        : "bg-primary/10 text-primary"
                    }`}>
                      {message.type === "user" ? (
                        <User className="w-4 h-4" />
                      ) : message.type === "system" ? (
                        <Shield className="w-4 h-4" />
                      ) : (
                        <Bot className="w-4 h-4" />
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <Card className={`p-4 ${
                        message.type === "user" 
                          ? "bg-primary text-primary-foreground" 
                          : message.type === "system"
                          ? "bg-muted/50"
                          : "bg-card"
                      }`}>
                        <div 
                          className="text-sm leading-relaxed max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: formatMessageContent(message.content)
                          }}
                        />
                      </Card>
                      
                      <div className={`flex items-center gap-2 mt-2 text-xs text-muted-foreground ${
                        message.type === "user" ? "justify-end" : ""
                      }`}>
                        <Clock className="w-3 h-3" />
                        <span>{message.timestamp.toLocaleTimeString()}</span>
                        
                        {message.metadata && (
                          <>
                            {message.metadata.confidence && (
                              <Badge variant="outline" className="text-xs px-1">
                                {message.metadata.confidence}% confidence
                              </Badge>
                            )}
                            {message.metadata.urgency && (
                              <Badge variant="outline" className={`text-xs px-1 ${getUrgencyColor(message.metadata.urgency)}`}>
                                {message.metadata.urgency} priority
                              </Badge>
                            )}
                            {message.metadata.jurisdiction && (
                              <Badge variant="outline" className="text-xs px-1">
                                {message.metadata.jurisdiction}
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                      
                      {message.metadata?.actionItems && (
                        <div className="mt-2 space-y-1">
                          <div className="text-xs font-medium text-muted-foreground">Recommended Actions:</div>
                          {message.metadata.actionItems.map((item, index) => (
                            <Button
                              key={index}
                              variant="outline"
                              size="sm"
                              className="text-xs h-6 px-2 mr-1 mb-1"
                              onClick={() => setNewMessage(`Help me with: ${item}`)}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {item}
                            </Button>
                          ))}
                        </div>
                      )}

                      {/* LLM Response Data Display */}
                      {message.llmResponse && (
                        <div className="mt-3 space-y-2">
                          {/* Context Status */}
                          <div className="flex items-center gap-2">
                            {message.llmResponse.context_loaded ? (
                              <Badge variant="outline" className="text-xs px-2 bg-green-500/10 text-green-400 border-green-500/20">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Evidence Package Loaded
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs px-2 bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                No Evidence Package
                              </Badge>
                            )}
                          </div>

                          {/* Package Info */}
                          {message.llmResponse.package_info && (
                            <div className="bg-muted/30 rounded-lg p-3 text-xs space-y-2">
                              <div className="font-medium text-muted-foreground">Evidence Package Info:</div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>Client: <span className="font-mono">{message.llmResponse.package_info?.client_name || 'Unknown'}</span></div>
                                <div>Case: <span className="font-mono">{message.llmResponse.package_info?.case_name || 'Unknown'}</span></div>
                                <div>Domains: <span className="font-medium">{
                                  message.llmResponse.package_info?.domains_analyzed?.length > 0 
                                    ? message.llmResponse.package_info.domains_analyzed.join(", ") 
                                    : "None"
                                }</span></div>
                                <div>Files: <span className="font-medium">{
                                  message.llmResponse.package_info?.files_available 
                                    ? (typeof message.llmResponse.package_info.files_available === 'number' 
                                        ? message.llmResponse.package_info.files_available 
                                        : message.llmResponse.package_info.files_available.length)
                                    : 0
                                }</span></div>
                              </div>
                            </div>
                          )}

                          {/* Sources Used */}
                          {message.llmResponse && (
                            <div className="bg-muted/30 rounded-lg p-3 text-xs space-y-2">
                              <div className="font-medium text-muted-foreground flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" />
                                Sources Referenced (1)
                              </div>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between bg-background/50 rounded px-2 py-1">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0"></div>
                                    <span className="truncate font-mono text-xs">Full Evidence Package</span>
                                    <Badge variant="outline" className="text-xs px-1 py-0">
                                      Evidence
                                    </Badge>
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    100%
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Error Display */}
                      {message.type === "error" && (
                        <div className="mt-3">
                          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertCircle className="w-4 h-4" />
                              <span className="font-medium text-sm">Error</span>
                            </div>
                            {message.error && (
                              <div className="text-xs text-red-300 font-mono bg-red-500/5 rounded p-2 mt-2">
                                {message.error}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="max-w-3xl mr-12">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4" />
                    </div>
                    <Card className="p-4 bg-card">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                        </div>
                        <span className="text-sm text-muted-foreground">Analyzing case data...</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Action Buttons - NEW: Added above input */}
      {activeCase && (
        <div className="border-t border-border bg-background/95 backdrop-blur-sm px-4 py-2">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
              {actionButtons.map((action, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => handleActionClick(action.prompt)}
                  className={`whitespace-nowrap h-8 px-3 text-xs transition-all duration-200 flex items-center gap-1.5 ${action.color}`}
                >
                  <action.icon className="w-3 h-3" />
                  {action.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="border-t border-border p-4 bg-background/95 backdrop-blur-sm">
        <div className="flex gap-3 max-w-4xl mx-auto">
          <div className="flex-1 relative">
            <Textarea
              ref={textareaRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={`Ask about case ${activeCase.title}...`}
              className="min-h-[44px] max-h-32 resize-none pr-56 py-3 bg-input-background border-border focus:border-primary transition-all duration-200"
              disabled={isLoading}
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
              Enter to send • Shift+Enter for new line
            </div>
          </div>
          
          <Button 
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isLoading}
            className="h-[44px] px-4 bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-200 hover:scale-105"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}